import React from 'react'

import { useUnit } from 'effector-react'

import { useMediaQueries, useMemo, useState } from 'hooks'

import { AuthFactor } from 'app/effector/2fa/types'

import { Logo } from 'components/_old/Logo/Logo.jsx'
import { Typo } from 'components/_old/Typo/Typo'

import { MobileLayout } from 'components/atoms/Layouts'
import { NavigationBar } from 'components/atoms/NavigationBar'
import { Paper } from 'components/atoms/Paper'
import { Typography } from 'components/atoms/Typography'

import { $currentStep, $hasBackStep, $isIssueShown, setIssueShown, goBack } from './model'
import { PushNotification } from './PushNotification'
import { TextMessage } from './TextMessage'
import { TwoFactorRecoveryCode } from './TwoFactorRecoveryCode'
import { $isNewRecoveryCodeShown } from './TwoFactorRecoveryCode/model'

type TwoFactorLoginAwaitProps = {
  query: Record<string, string>
  setIsPhoneVerified?: (flag: boolean) => void
  onAccept: () => void
  onDeny: () => void
  onError: () => void
  onCancel: () => void
}

const TwoFactorLoginAwait = ({
  query,
  setIsPhoneVerified,
  onAccept,
  onDeny,
  onError,
  onCancel,
}: TwoFactorLoginAwaitProps): React.ReactElement => {
  const { desktop } = useMediaQueries()

  const [currentStep, isIssueShown, hasBackStep, handleSetIssueShown, handleGoBack, isNewRecoveryCodeShown] = useUnit([
    $currentStep,
    $isIssueShown,
    $hasBackStep,
    setIssueShown,
    goBack,
    $isNewRecoveryCodeShown,
  ])

  const [shouldRememberBrowser, setShouldRememberBrowser] = useState(false)

  const handleBack = (): void => {
    if (isIssueShown) {
      handleSetIssueShown(false)
      return
    }

    if (hasBackStep) {
      handleGoBack()
      return
    }

    onCancel()
  }

  const content = useMemo(() => {
    if (currentStep === AuthFactor.PUSH) {
      return (
        <Paper top={32} right={desktop ? 64 : 16} left={desktop ? 64 : 16} bottom={56}>
          <PushNotification query={query} onAccept={onAccept} onDeny={onDeny} onError={onError} />
        </Paper>
      )
    }

    if (currentStep === AuthFactor.SMS) {
      return (
        <TextMessage
          query={query}
          setIsPhoneVerified={setIsPhoneVerified}
          onAccept={onAccept}
          shouldRememberBrowser={shouldRememberBrowser}
          setShouldRememberBrowser={setShouldRememberBrowser}
        />
      )
    }

    if (currentStep === AuthFactor.RECOVERY_CODE) {
      return <TwoFactorRecoveryCode query={query} onAccept={onAccept} shouldRememberBrowser={shouldRememberBrowser} />
    }

    return null
  }, [
    desktop,
    currentStep,
    onAccept,
    onDeny,
    onError,
    query,
    setIsPhoneVerified,
    shouldRememberBrowser,
    setShouldRememberBrowser,
  ])

  if (desktop) {
    return (
      <Paper style={{ minHeight: '600px' }}>
        <Paper top={20} right={24} bottom={24} left={24}>
          <NavigationBar leftPartText="Back" onLeftPartClick={isNewRecoveryCodeShown ? undefined : handleBack}>
            <Typography size={20} weight="semibold">
              <Typo>Two-factor authentication</Typo>
            </Typography>
          </NavigationBar>
        </Paper>
        {content}
      </Paper>
    )
  }

  return (
    <MobileLayout
      header={
        <NavigationBar leftPartText="Back" onLeftPartClick={isNewRecoveryCodeShown ? undefined : handleBack}>
          <Logo className="NavigationBar-Logo" style={{ height: 24 }} />
        </NavigationBar>
      }
      content={content}
      contentPaperSizes={{ left: 0, right: 0, bottom: 0 }}
    />
  )
}

export { TwoFactorLoginAwait }
